import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { Select, Store } from '@ngxs/store';
import { CustomIcons } from '@shared/models/custom-icons';
import { MoreVertMenuItem } from '@shared/models/more-vert-menu-item';
import { getAllChildTagsForTd } from '@utils/other-utils';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ScrollDispatcher } from '@angular/cdk/overlay';
import {
  ChangeCatalogFilter,
  ClearCatalogs,
  GetCatalogs,
  GetCatalogsCanceled,
  GetExtraCatalog
} from 'app/modules/data/modules/catalog-data/store/catalog-table.actions';
import { ChangedSelectCatalog } from '@shared/state/catalog/shared-catalog.action';
import { CatalogView } from '@shared/models/shared-catalog.state';
import { UserInfo } from '@auth/models/auth.state';
import { AuthState } from '@auth/store/auth/auth.state';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { cloneDeep } from 'lodash';
import { ServerLinks } from '@shared/services/server-links';
import { ServerLinkName } from '@shared/models/server-links-model';
import { SharedState } from '@shared/state/shared/shared.state';
import { Locale } from '@shared/models/locale';
import { TranslateService } from '@ngx-translate/core';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { CatalogTableState } from '../../store/catalog-table.state';
import {
  AddNotificationCatalog,
  DeleteNotificationCatalog,
  GetNotificationCatalogs
} from '../../../../store/catalog-change-notifications/catalog-change-notifications.action';
import { CatalogChangeNotificationsState } from '../../../../store/catalog-change-notifications/catalog-change-notifications.state';
import { AddFavoriteCatalog, DeleteFavoriteCatalog, GetFavoriteCatalogs } from '../../../../store/favorite-catalogs/favorite-catalogs.action';
import { FavoriteCatalogsState } from '../../../../store/favorite-catalogs/favorite-catalogs.state';
import { GetCatalogsExportStatus } from '../../../../store/catalog/catalog.action';
import { DownloadButtonComponent } from '../../../../shared/components/download-button/component/download-button.component';
import { CatalogFilter } from '../../models/catalog.service';
import { CatalogItemsDataRouting } from '../../../catalog-items-data/models/catalog-items-data-routing';

@Component({
  selector: 'app-catalog-table',
  templateUrl: './catalog-table.component.html',
  styleUrls: ['./catalog-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class CatalogTableComponent implements OnInit, AfterViewInit, IDestroy, OnDestroy {
  private _isMobile = false;
  private _scrollLoading: boolean;
  private _currentUser: UserInfo | null;
  public componentDestroy: () => Subject<any>;
  public dataSource: MatTableDataSource<CatalogView> = new MatTableDataSource<CatalogView>([]);
  public menuItems: MoreVertMenuItem[];
  public catalogData$: Observable<CatalogView[]>;
  public scrollDispatcher$: Observable<any>;
  public firstOpen = true;
  public isRuLocale = true;
  public langChange$: Observable<LangChangeEvent>;
  public icons = CustomIcons;
  @ViewChild(DownloadButtonComponent) public downloadButtonComponent: DownloadButtonComponent;
  public tableScroll: HTMLElement | null;
  @Select(actionsExecuting([GetCatalogs, GetNotificationCatalogs, GetFavoriteCatalogs])) public loadingData$: Observable<ActionsExecuting>;
  public loadingExtraData = false;
  @Select(CatalogTableState.getFatalError) public isFatalError$: Observable<boolean>;
  public displayedColumns: string[] = [];

  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 1366px)', '(max-width: 1024px)', '(max-width: 830px)']).pipe(
    map((state: BreakpointState) => {
      this._isMobile = false;
      const minimalDisplayedColumns = ['catalogName', 'notificationFavoriteIcon', 'rating'];
      const lastColumns = ['export', 'favorites'];
      if (state.breakpoints['(max-width: 830px)']) {
        this.displayedColumns = ['catalogName'];
        this._isMobile = true;
        return 830;
      }

      if (state.breakpoints['(max-width: 1024px)']) {
        this.displayedColumns = [...minimalDisplayedColumns, ...lastColumns];
        return 1024;
      }

      if (state.breakpoints['(max-width: 1366px)']) {
        this.displayedColumns = [...minimalDisplayedColumns, 'numberDownloads', 'relevance', 'notes', ...lastColumns];
        return 1366;
      }

      this.displayedColumns = [
        'catalogName',
        'notificationFavoriteIcon',
        'categoryName',
        'rating',
        'numberView',
        'numberDownloads',
        'relevance',
        'notes',
        ...lastColumns
      ];
      return 1367;
    })
  );
  public currentFilter$: Observable<CatalogFilter | null>;
  public fileLink: string;

  constructor(
    private router: Router,
    private store: Store,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private scrollDispatcher: ScrollDispatcher,
    public dialog: MatDialog,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    serverLinks: ServerLinks
  ) {
    this.fileLink = serverLinks.getLink(ServerLinkName.DownloadFile);
  }

  private getDataSetHref(element: CatalogView): string {
    let url = `opendata/${element.datasetId}`;
    if (this.translateService.currentLang === Locale.EN_US) {
      url += `/${CatalogItemsDataRouting.Passport}`;
    }
    url += `?isDynamic=${element.isDynamic}`;
    return url;
  }

  private getNotificationAndFavoriteIcon(hasNotification: boolean, hasFavorite: boolean): CustomIcons | null {
    if (hasNotification && hasFavorite) {
      return CustomIcons.NotificationsAndFavorite;
    }
    if (hasNotification) {
      return CustomIcons.FillNotifications;
    }
    if (hasFavorite) {
      return CustomIcons.BookmarkFill;
    }
    return null;
  }

  private changeFavoriteAndNotificationIcon(currentCatalogId: number): void {
    const currentRow = this.dataSource.data.find(row => row.id === currentCatalogId);
    if (currentRow) {
      const hasNotification = (this.store.selectSnapshot(CatalogChangeNotificationsState.notificationCatalogIds) ?? []).some(
        id => id === currentCatalogId
      );
      const hasFavorite = (this.store.selectSnapshot(FavoriteCatalogsState.favoriteCatalogIds) ?? []).some(id => id === currentCatalogId);
      currentRow.notificationFavoriteIcon = this.getNotificationAndFavoriteIcon(hasNotification, hasFavorite);
      this.cdr.detectChanges();
    }
  }

  private getFavoriteMenuItem(element: CatalogView): MoreVertMenuItem | null {
    const favoriteCatalogIds = this.store.selectSnapshot(FavoriteCatalogsState.favoriteCatalogIds);
    const currentCatalogId = element.id;
    const isFavorite = !!favoriteCatalogIds?.find(catalogId => catalogId === currentCatalogId);

    if (!this._currentUser && isFavorite) {
      return null;
    }
    return {
      title: isFavorite ? 'DATA.SHARED.MORE_VERT_MENU.DELETE_FROM_FAVORITES' : 'DATA.SHARED.MORE_VERT_MENU.ADD_TO_FAVORITES',
      click: () => {
        if (this._currentUser) {
          this.store
            .dispatch(isFavorite ? new DeleteFavoriteCatalog(currentCatalogId) : new AddFavoriteCatalog(currentCatalogId))
            .pipe(takeUntil(this.componentDestroy()))
            .subscribe(() => {
              this.changeFavoriteAndNotificationIcon(currentCatalogId);
            });
        } else {
          const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
          authorizationDialog.componentInstance.keyOperations = 'AddFavoriteItem';
          authorizationDialog.componentInstance.operationValue = currentCatalogId;
        }
      },

      icon: isFavorite ? CustomIcons.BookmarkFill : CustomIcons.Bookmark
    };
  }

  private getNotificationMenuItem(element: CatalogView): MoreVertMenuItem | null {
    const subscribeCatalogIds = this.store.selectSnapshot(CatalogChangeNotificationsState.notificationCatalogIds);
    const currentCatalogId = element.id;
    const isSubscribe = !!subscribeCatalogIds?.find(catalogId => catalogId === currentCatalogId);

    if (!this._currentUser && isSubscribe) {
      return null;
    }
    return {
      title: isSubscribe ? 'DATA.SHARED.MORE_VERT_MENU.UNSUBSCRIBE_FROM_CHANGES' : 'DATA.SHARED.MORE_VERT_MENU.SUBSCRIBE_FOR_CHANGES',
      click: () => {
        if (this._currentUser) {
          this.store
            .dispatch(isSubscribe ? new DeleteNotificationCatalog(currentCatalogId) : new AddNotificationCatalog(currentCatalogId))
            .pipe(takeUntil(this.componentDestroy()))
            .subscribe(() => {
              this.changeFavoriteAndNotificationIcon(currentCatalogId);
            });
        } else {
          const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
          authorizationDialog.componentInstance.keyOperations = 'AddNotification';
          authorizationDialog.componentInstance.operationValue = currentCatalogId;
        }
      },

      icon: isSubscribe ? CustomIcons.FillNotifications : CustomIcons.Notifications
    };
  }

  private setFavoriteAndNotificationIcon(data: CatalogView[]): CatalogView[] {
    const notificationCatalogIds = this.store.selectSnapshot(CatalogChangeNotificationsState.notificationCatalogIds);
    const favoriteCatalogIds = this.store.selectSnapshot(FavoriteCatalogsState.favoriteCatalogIds);
    const categories = this.store.selectSnapshot(SharedState.getCategories);
    return data.map(res => {
      const hasNotification = (notificationCatalogIds ?? []).some(id => id === res.id);
      const hasFavorite = (favoriteCatalogIds ?? []).some(id => id === res.id);
      res.notificationFavoriteIcon = this.getNotificationAndFavoriteIcon(hasNotification, hasFavorite);
      res.imageCode = categories.find(x => x.idOpenCategory === res.categoryId)?.imageCode ?? null;
      return res;
    });
  }

  private getFirstCatalogData(): Observable<any> {
    return this.store.select(CatalogTableState.getAllCatalogs).pipe(
      tap(data => {
        let sliceCount = this.dataSource.data.length;
        if (data.length <= this.dataSource.data.length) {
          this.dataSource.data = [];
          sliceCount = 0;
        }

        const newData = this.setFavoriteAndNotificationIcon(cloneDeep(data).splice(sliceCount));
        this.setText(this.translateService.currentLang as Locale, newData);
        this.insertTableRow(newData);

        this.store.dispatch(new GetCatalogsExportStatus(data.map(x => x.id)));
      })
    );
  }

  private insertTableRow(newData: CatalogView[]) {
    const insertCount = 20;

    // eslint-disable-next-line no-param-reassign
    newData = newData.map(res => ({
      ...res,
      link: this.getDataSetHref(res)
    }));
    let count = newData.length;

    if (this.dataSource.data.length === 0) {
      count -= insertCount;
      this.dataSource.data = newData.splice(0, insertCount);
    }

    while (count > 0) {
      setTimeout(() => {
        this.dataSource.data = [...this.dataSource.data, ...newData.splice(0, insertCount)];
      });

      count -= insertCount;
    }
  }

  private getMenuItems(element: CatalogView): MoreVertMenuItem[] {
    const resultMenu: MoreVertMenuItem[] = [];

    const favoriteMenu = this.getFavoriteMenuItem(element);
    if (favoriteMenu) {
      resultMenu.push(favoriteMenu);
    }

    if (this.isRuLocale) {
      const subscribeMenu = this.getNotificationMenuItem(element);
      if (subscribeMenu && !element.isDynamic) {
        resultMenu.push(subscribeMenu);
      }
    }

    return resultMenu;
  }

  private setText(lang: Locale, data: CatalogView[] | null = null): void {
    if (lang === Locale.RU_RU) {
      (data ?? this.dataSource.data).forEach(x => {
        x.catalogName = x.catalogNameRu;
        x.categoryName = x.categoryNameRu;
        x.link = this.getDataSetHref(x);
      });

      this.isRuLocale = true;
    } else {
      this.isRuLocale = false;
      (data ?? this.dataSource.data).forEach(x => {
        x.catalogName = x.catalogNameEn;
        x.categoryName = x.categoryNameEn;
        x.link = this.getDataSetHref(x);
      });
    }
    this.cdr.detectChanges();
  }

  public ngOnInit(): void {
    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );

    this.currentFilter$ = this.store.select(CatalogTableState.getCatalogFilter).pipe(
      tap(() => {
        const isLoad = this.store.selectSnapshot(CatalogTableState.getLoadingDataBeforeChangeFilter);
        if (isLoad) {
          this.dataSource = new MatTableDataSource<CatalogView>([]);
        }
      })
    );

    this._currentUser = this.store.selectSnapshot(AuthState.authenticatedUser);
    this.store
      .select(CatalogTableState.getCatalogUpdate)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe(updateCatalog => {
        if (updateCatalog && this.dataSource && this.dataSource.data) {
          let newData: CatalogView[] = cloneDeep(this.dataSource.data);
          const findedCatalogIndex = newData.findIndex(x => x.id === updateCatalog.id);
          newData[findedCatalogIndex] = updateCatalog;
          newData = newData.map(res => ({
            ...res,
            link: this.getDataSetHref(res)
          }));
          this.dataSource.data = newData;
        }
      });

    if (isPlatformServer(this.platformId)) {
      this.dataSource = new MatTableDataSource<CatalogView>([]);
    }
  }

  public ngAfterViewInit(): void {
    this.scrollDispatcher$ = this.scrollDispatcher.scrolled().pipe(
      filter(() =>
        this.tableScroll && !this._scrollLoading && this.tableScroll.scrollHeight > this.tableScroll.clientHeight
          ? Math.abs(this.tableScroll.scrollHeight - (this.tableScroll.clientHeight + this.tableScroll.scrollTop)) < 100
          : false
      ),
      filter(() => this.store.selectSnapshot(CatalogTableState.getCatalogTotal) > this.dataSource.data.length),
      debounceTime(300),
      switchMap(() => {
        this.loadingExtraData = true;
        this._scrollLoading = true;
        this.cdr.detectChanges();
        setTimeout(() => {
          if (this.tableScroll && this.tableScroll.scrollHeight) {
            this.tableScroll?.scrollTo(0, this.tableScroll.scrollHeight);
          }
        });
        return this.store.dispatch(new GetExtraCatalog());
      }),
      tap(() => {
        this._scrollLoading = false;
        setTimeout(() => {
          this.loadingExtraData = false;
          this.cdr.detectChanges();
        });
      })
    );
    this.tableScroll = this.document.getElementById('table-scroll-container');
  }

  public trackRow(index: number, row: any): string {
    return row.id;
  }

  public loadingCatalog(): void {
    this.store
      .dispatch(new GetCatalogs())
      .pipe(take(1), takeUntil(this.componentDestroy()))
      .subscribe(() => {
        if (this._currentUser?.token) {
          this.store
            .dispatch([new GetNotificationCatalogs(), new GetFavoriteCatalogs()])
            .pipe(
              take(1),
              tap(() => {
                this.getFirstCatalogData().pipe(takeUntil(this.componentDestroy())).subscribe();
              })
            )
            .subscribe();
        } else {
          this.catalogData$ = this.getFirstCatalogData();
        }
        this.firstOpen = false;
        this.cdr.detectChanges();
      });
  }

  public selectCatalog(event: MouseEvent | null, selectedCatalog: CatalogView): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const topTags = ['APP-DOWNLOAD-BUTTON', 'APP-MORE-VERT-MENU', 'SPAN-CATEGORY'];
    const parentTags = getAllChildTagsForTd(topTags, event?.target as HTMLElement);
    if (!parentTags.some(parentTag => topTags.some(topTag => topTag === parentTag))) {
      this.store.dispatch(new ChangedSelectCatalog(selectedCatalog));
      let url = `opendata/${selectedCatalog.datasetId}`;
      if (this.translateService.currentLang === Locale.EN_US) {
        url += `/${CatalogItemsDataRouting.Passport}`;
      }
      this.router.navigate([url], {
        queryParams: { isDynamic: selectedCatalog.isDynamic }
      });
    }
  }

  public openTableMenu(element: CatalogView): void {
    this.menuItems = this.getMenuItems(element);
  }

  public mobileTap(element: CatalogView): void {
    if (this._isMobile) {
      this.downloadButtonComponent.catalogId = element.id;
      this.downloadButtonComponent.catalog = element;
      this.downloadButtonComponent.isDynamic = element.isDynamic;
      this.downloadButtonComponent.openModal();
    }
  }

  public setCategoryFilter(event: MouseEvent, element: CatalogView): void {
    event.stopPropagation();
    event.preventDefault();
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          openCategoryIds: [element.categoryId]
        },
        true
      )
    );
  }

  public ResetFilter(): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          catalogIds: [],
          booleanFilters: {
            activeStatus: true,
            archiveStatus: null,
            withMap: null,
            withoutMap: null,
            iso: null,
            summerSeason: null,
            regionalDict: null,
            ordinaryDict: null,
            federalDict: null,
            datasetType: null,
            allSeasons: null,
            accessibility: null,
            winterSeason: null
          },
          openCategoryIds: [],
          providerIds: [],
          newRecommendedFavorites: 'all',
          searchString: ''
        },
        true
      )
    );
  }

  public ngOnDestroy(): void {
    this.store.dispatch([new GetCatalogsCanceled(), new ClearCatalogs()]);
  }
}
